import {
  EActionBoxCodeEvent,
  ESessionPhase,
  TBoxBodyPublic,
  TCards,
} from '@lbs/shared-rp-types'
import { makeAutoObservable } from 'mobx'
import { BoxUIStore } from './BoxUI.store'
import { RussianPokerStore } from './index'

export interface IBox extends TBoxBodyPublic {}

export class BoxStore implements IBox {
  rootStore: RussianPokerStore

  ui: BoxUIStore

  seatNumber!: number

  pendingCardsRequest!: TBoxBodyPublic['pendingCardsRequest']

  decision!: TBoxBodyPublic['decision']

  ante!: TBoxBodyPublic['ante']

  bonus!: TBoxBodyPublic['bonus']

  bet!: TBoxBodyPublic['bet']

  cards!: TBoxBodyPublic['cards']

  hand!: TBoxBodyPublic['hand']

  switchCards!: TBoxBodyPublic['switchCards']

  buySixCard!: TBoxBodyPublic['buySixCard']

  insurance!: TBoxBodyPublic['insurance']

  result!: TBoxBodyPublic['result']

  buyDealerGame!: TBoxBodyPublic['buyDealerGame']

  selected!: TBoxBodyPublic['selected']

  cardsForSwitch: TCards[] = []

  // Инкрементируется только при релевантном success-патче действия бокса
  // (insurance/switch/buySixCard/pendingCardsRequest). Сигнал для снятия submitting-lock.
  lastSuccessfulActionPatchId: number = 0

  constructor(payload: TBoxBodyPublic, rootStore: RussianPokerStore) {
    makeAutoObservable(this, { rootStore: false })

    this.rootStore = rootStore
    this.ui = new BoxUIStore(this)

    Object.assign(this, payload)
  }

  patch = (payload: Partial<TBoxBodyPublic>): void => {
    Object.assign(this, payload)
  }

  markActionPatchResolved = (): void => {
    this.lastSuccessfulActionPatchId += 1
  }

  patchStore = (payload: Partial<BoxStore>): void => {
    Object.assign(this, payload)
  }

  get target() {
    return {
      gameId: this.rootStore.game.gameId,
      sessionId: this.rootStore.game.sessionId,
      seatNumber: this.seatNumber,
    }
  }

  // есть ли ставки на боксе
  get hasStake(): boolean {
    return Boolean(this.ante) || Boolean(this.bonus.amount) || Boolean(this.bet)
  }

  get sumStake(): number {
    return this.ante + this.bet + this.bonus.amount
  }

  get hasCombination(): boolean {
    return this.hand.handName !== 'HIGH_CARD'
  }

  get isDecisionPhase(): boolean {
    return this.rootStore.session.phase === ESessionPhase.DECISIONS
  }

  get isSettledPhase(): boolean {
    return this.rootStore.session.phase === ESessionPhase.SETTLEMENT
  }

  get isFinishedPhase(): boolean {
    return this.rootStore.session.phase === ESessionPhase.FINISHED
  }

  // внутри принятие решения ожидаются дополнительные карты: 6 карта
  // либо обмен карт и это состояние в промежутке когда нажали на действие, но карты еще не пришли
  get isPendingCardRequest(): boolean {
    return Boolean(this.pendingCardsRequest)
  }

  get isSwitchOrBuyAvailable(): boolean {
    return this.switchCards.available || this.buySixCard.available
  }

  get isSwitchAvailable(): boolean {
    return this.switchCards.available
  }

  get isInsuranceAvailable(): boolean {
    return this.insurance.available && this.isMyTurn
  }

  get isBuySixCardAvailable(): boolean {
    return this.buySixCard.available
  }

  get isMyTurn(): boolean {
    return this.rootStore.session.boxOnTurn === this.seatNumber
  }

  toggleCardsForSwitch = (card: TCards): void => {
    const indexCurrentCard = this.cardsForSwitch.indexOf(card)

    if (indexCurrentCard === -1) {
      this.cardsForSwitch.push(card)
    } else {
      this.cardsForSwitch.splice(indexCurrentCard, 1)
    }
  }

  sendEventWithContext = (payload: {
    event: EActionBoxCodeEvent
    data?: object
  }): void => {
    this.rootStore.emitBox({
      target: this.target,
      event: payload.event,
      data: {
        ...payload.data,
      },
    })
  }

  sendSelectedBox = (): void => {
    this.sendEventWithContext({
      event: EActionBoxCodeEvent.TAKE_SEAT,
    })
  }

  sendBuyDealerGame = (accepted: boolean): void => {
    this.sendEventWithContext({
      event: EActionBoxCodeEvent.BUY_DEALER_GAME,
      data: {
        accepted,
      },
    })
  }

  sendPlaceAnte = (customAmount?: number): void => {
    const amount = customAmount || this.rootStore.selectedChip

    if (amount <= 0) return

    this.sendEventWithContext({
      event: EActionBoxCodeEvent.PLACE_ANTE,
      data: {
        amount: customAmount || this.rootStore.selectedChip,
      },
    })
  }

  sendPlaceBonus = (): void => {
    this.sendEventWithContext({
      event: EActionBoxCodeEvent.PLACE_BONUS,
      data: {
        amount: this.rootStore.selectedChip,
      },
    })
  }

  sendRollbackLastStake = (): void => {
    this.sendEventWithContext({
      event: EActionBoxCodeEvent.ROLLBACK_LAST_STAKE,
    })
  }

  sendRollbackAllStakes = (): void => {
    this.sendEventWithContext({
      event: EActionBoxCodeEvent.ROLLBACK_ALL_STAKES,
    })
  }

  sendInsurance = (amount: number): void => {
    this.sendEventWithContext({
      event: EActionBoxCodeEvent.INSURANCE,
      data: {
        amount,
      },
    })
  }

  sendBuySixCard = (): void => {
    this.sendEventWithContext({
      event: EActionBoxCodeEvent.BUY_SIX_CARD,
    })
  }

  sendSwitchCards = (switchedCards: Array<TCards>): void => {
    this.sendEventWithContext({
      event: EActionBoxCodeEvent.SWITCH_CARDS,
      data: {
        switchedCards,
      },
    })
  }

  sendPass = (): void => {
    this.sendEventWithContext({
      event: EActionBoxCodeEvent.PASS,
    })
  }

  sendBet = (): void => {
    this.sendEventWithContext({
      event: EActionBoxCodeEvent.BET,
    })
  }
}
